import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Group, Select, Stack, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { registerLot, updateLot, type LotDto } from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Null to register a new one. */
  lot: LotDto | null;
  /** Lot-controlled items, for the picker on a new lot. */
  itemOptions: { value: string; label: string }[];
  itemsLoading: boolean;
  onSaved: () => void;
}

/** A date input wants `yyyy-mm-dd`; the API speaks ISO instants. */
function toDateInput(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

function toIsoOrNull(date: string): string | null {
  return date ? `${date}T00:00:00Z` : null;
}

/**
 * Registers a lot, or corrects one.
 *
 * **The item and the lot number are fixed once registered**, so on an edit they are shown and
 * disabled rather than hidden. They are the lot's identity: every ledger row, pick line and
 * packing list that names this lot points at it, and correcting a lot number is a new lot and a
 * transfer rather than an edit. The server has no parameter for either (tenant script 082).
 *
 */
export function LotFormModal({ opened, onClose, lot, itemOptions, itemsLoading, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = lot !== null;

  const [itemId, setItemId] = useState<string | null>(null);
  const [lotNumber, setLotNumber] = useState('');
  const [fifoDate, setFifoDate] = useState('');
  const [expiry, setExpiry] = useState('');
  const [supplierLot, setSupplierLot] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setSubmitError(null);
    setItemId(lot?.itemId ?? null);
    setLotNumber(lot?.lotNumber ?? '');
    setFifoDate(toDateInput(lot?.fifoDateUtc));
    setExpiry(toDateInput(lot?.expirationDateUtc));
    setSupplierLot(lot?.supplierLotNumber ?? '');
  }, [opened, lot]);

  const save = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return updateLot(lot!.lotId, {
          // Never blank: it is what decides which lot is picked first, and the server refuses
          // a null. Falling back to what the lot already has keeps a correction to the expiry
          // from quietly rewriting the picking order.
          fifoDateUtc: toIsoOrNull(fifoDate) ?? lot!.fifoDateUtc,
          expirationDateUtc: toIsoOrNull(expiry),
          supplierLotNumber: supplierLot.trim() || null,
        });
      }
      return registerLot({
        itemId: itemId!,
        lotNumber: lotNumber.trim().toUpperCase(),
        fifoDateUtc: toIsoOrNull(fifoDate),
        expirationDateUtc: toIsoOrNull(expiry),
        supplierLotNumber: supplierLot.trim() || null,
      });
    },
    onSuccess: () => {
      notifications.show({ message: isEdit ? t('lots.saved') : t('lots.registered'), color: 'green' });
      onSaved();
      onClose();
    },
    // The server's own sentence when it has one: each refusal names the item or the lot and
    // says what to do instead.
    onError: (err) => setSubmitError(
      err instanceof ApiError
        ? (err.problem.detail ?? err.problem.title ?? t('lots.errors.saveFailed'))
        : t('lots.errors.saveFailed'),
    ),
  });

  const canSave = isEdit || (Boolean(itemId) && lotNumber.trim().length > 0);

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('lots.editTitle', { lot: lot!.lotNumber }) : t('lots.newTitle')}
      width="md"
    >
      <Stack gap="md">
        <Select
          label={t('lots.item')}
          data={itemOptions}
          value={itemId}
          onChange={setItemId}
          // Shown and disabled on an edit rather than hidden: which item a lot belongs to is
          // the first thing somebody checks, and hiding it to express "you cannot change this"
          // also hides the answer.
          disabled={isEdit || itemsLoading}
          description={isEdit ? t('lots.identityFixed') : undefined}
          searchable
          required={!isEdit}
          nothingFoundMessage={t('shell.noMatch')}
        />

        <TextInput
          label={t('lots.lotNumber')}
          value={lotNumber}
          onChange={(e) => setLotNumber(e.currentTarget.value.toUpperCase())}
          disabled={isEdit}
          maxLength={50}
          required={!isEdit}
        />

        <TextInput
          type="date"
          label={t('lots.fifoDate')}
          description={t('lots.fifoDateHint')}
          value={fifoDate}
          onChange={(e) => setFifoDate(e.currentTarget.value)}
        />

        <TextInput
          type="date"
          label={t('lots.expiry')}
          description={t('lots.expiryHint')}
          value={expiry}
          onChange={(e) => setExpiry(e.currentTarget.value)}
        />

        <TextInput
          label={t('lots.supplierLot')}
          description={t('lots.supplierLotHint')}
          value={supplierLot}
          onChange={(e) => setSupplierLot(e.currentTarget.value)}
          maxLength={100}
        />

        {submitError && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            {submitError}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('shell.cancel')}</Button>
          <Button
            color="invetrak"
            loading={save.isPending}
            disabled={!canSave}
            onClick={() => save.mutate()}
          >
            {isEdit ? t('shell.saveChanges') : t('lots.register')}
          </Button>
        </Group>
      </Stack>
    </AppModal>
  );
}
